import { ca } from '../i18n/ca';
import { formatSkill } from './enemySkill';

/**
 * The quick start's draft and the shape of its rail. Separate from the screens
 * so the components file exports components only (the react-refresh rule; the
 * same reason natoSymbols.ts sits beside NatoSymbol.tsx).
 */
/**
 * What the first three steps decide; steps 4 and 5 read it from session
 * storage.
 *
 * <p>The three used to be one screen. They are separate now because each one
 * carries a details panel: a preset is a mod set with a catalogue behind it, a
 * terrain has a size and a list of towns to build the mission around, and an
 * enemy has a side and a strength worth setting by hand. All three still write
 * into this one draft, so a reload keeps what was chosen and the later steps
 * read the shape they always read.
 */
export interface MissionDraftStep1 {
  /**
   * The server the night is played on: step 1's answer, and the mod set with
   * it. The hash is the identity of what that server runs — Workshop mods and
   * Creator DLCs — and the key of the catalogue every later step reads.
   */
  serverId: number;
  serverName: string;
  modSetHash: string;
  /** When the night is set: one of the eras the server can carry, its own proposal unless somebody picks another. */
  era?: string;
  worldName: string;
  /** The terrain's display name, for the rail; the world name when the scan had none. */
  worldDisplayName?: string;
  /**
   * Which fire-support system the preset plays with, from its readiness answer.
   * 'simplex' when it carries Simplex Support Services, which is what the group
   * plays with: the supports card then asks whether a support is there at all
   * instead of how many calls it is worth, because Simplex counts nothing —
   * it holds a service back with a cooldown and never runs out of it.
   */
  supportsSystem?: 'simplex' | 'vanilla' | null;
  enemyProfileId: number | null;
  enemyFactionClass: string | null;
  /** The enemy profile's name, for the rail. */
  enemyName?: string | null;
  /** The settlement the mission is built around, or null to let the seed choose. */
  preferredPlace?: string | null;
  /** light · medium · heavy, overriding the recipe's, or null. */
  enemyStrength?: string | null;
  /**
   * 0.1..1. The difficulty step always writes the number it shows (2026-10-12),
   * so the strength never sets it; null only in a draft from before, where the
   * generator still takes it from the strength.
   */
  enemySkill?: number | null;
  /** «Els enemics troben els seus morts»: on unless switched off here. */
  enemiesFindBodies?: boolean | null;
  /**
   * A composition preset built at every objective the enemy holds: sandbags,
   * wire, a checkpoint's own props. Null leaves the position as bare ground,
   * which is what an author never does.
   */
  enemyDefences?: string | null;
  /** Draw the zone the enemy is expected in. */
  intelArea?: boolean | null;
  /** Mark the positions worth expecting trouble at. */
  intelStrongpoints?: boolean | null;
  /** vague · reasonable · good: how far a mark sits from the truth. */
  intelQuality?: string | null;
  /** How many strongpoints are marked. */
  intelMarks?: number | null;
  /** How many marks point at nobody at all. */
  intelFalse?: number | null;

  // --- our own forces (step 6) ---------------------------------------------
  //
  // The quick start used to send `playerSide: 'west'` and `playerFaction: null`
  // on every generation, so a group that plays as AAF, as the Resistance or as a
  // mod's own unit had to fix each mission after it was made. These are what the
  // step that describes our forces writes; all of them may be absent, and then
  // the generator does exactly what it did. The vehicles and the base it also
  // wrote moved to step 7 on 2026-09-10 (MissionDraftStart).
  /** west · east · resistance. */
  playerSide?: string | null;
  /** Our own CfgFactionClasses class. */
  playerFactionClass?: string | null;
  /** Its display name, for the rail. */
  playerFactionName?: string | null;
  /**
   * The profile picked, when two share a class: a hand-made faction (the 1RA)
   * plays as USMC and must not be read back as the USMC it was cloned from.
   */
  playerProfileId?: number | null;
  /** How many slots make one squad; null for the definition's eight. */
  squadSize?: number | null;
  /** What one squad is made of, in order; null or empty leaves the recipe's own table. */
  roles?: PlayerRoleDraft[] | null;
  /** Who flies the helicopter, from a faction's own squad; null for the faction's pilot class. */
  aircrew?: PlayerRoleDraft | null;
}

/** One line of our own order of battle, as the step edits it. */
export interface PlayerRoleDraft {
  role: string;
  label?: string;
  /** An exact man class from the faction's own assets; null for the catalogue's default. */
  unitClass?: string | null;
  /** A saved loadout's id, applied to whoever takes the slot. */
  loadoutId?: string | null;
  count?: number;
}

export const DRAFT_KEY = 'ops.newMission.step1';
export const STEP2_KEY = 'ops.newMission.step2';
/** What step 7, "Sortida", decides. */
export const START_KEY = 'ops.newMission.start';
/**
 * What the last step decides. It was the only step that kept nothing, so an
 * "Enrere" to look at the squad and a "Següent" back cost the server, the
 * twists and the title (QUICK_START_REVIEW.md F11).
 */
export const DECIDE_KEY = 'ops.newMission.decide';
/** The members invited to edit the mission, kept until it exists and they can be added to it. */
export const EDITORS_KEY = 'ops.newMission.editors';

/** A member picked in the wizard's list of invited editors. */
export interface DraftEditor {
  handle: string;
  displayName: string;
}

/**
 * What the type step decides. It lived inside `NewMissionSteps.tsx` while it was
 * the only screen that read it; the step for our own forces reads it too — the
 * squad it edits is the one the recipe proposes, filled to the number of people
 * actually coming.
 */
export interface MissionDraftStep2 {
  recipeId: string;
  /** For the rail's summary; the recipe list is not fetched again to show it. */
  recipeTitle?: string;
  players: number;
  hour: number | null;
  weather: string | null;
  /** The squad the recipe proposes, first slot first, so step 6 starts from it. */
  recipeRoles?: string[];
  /**
   * Who decides the secondary objectives: the seed by each one's chance, the
   * creator (exactly `secondaryObjectives`), or a vote before the session
   * between them. Absent is the seed, which is what every mission did.
   */
  secondaryBy?: 'chance' | 'creator' | 'voteBefore';
  /** The ids chosen or put to the vote; null leaves them to the seed. */
  secondaryObjectives?: string[] | null;
  /**
   * The ground the type needs (`airfield`, `site`, `bridge`, `road`…), so the
   * map step, which now follows the type, can mark the terrains that lack it.
   * Absent for a type that needs nothing in particular.
   */
  needs?: string | null;
  /**
   * What the type asks of the enemy (release D, D5.5): irregular only, with the
   * reason the enemy step greys every other faction with. Absent: any enemy.
   */
  enemyRule?: { irregular: boolean; reason: string } | null;
}

/**
 * What step 7, "Sortida", decides: where the night starts, the ways in and out
 * and who picks between them, and the vehicles for them. These were three
 * answers on two screens that could contradict each other — a base-model
 * select, a land-or-sea radio and a set of insertion boxes all ticked — and
 * are one now (QUICK_START_REVIEW.md F2, F3, F4).
 */
export interface MissionDraftStart {
  /** The recipe the ways in were offered from; another recipe offers others. */
  recipeId: string;
  /** Land, the sea, or a point of the creator's own on the map (2026-09-13). */
  startFrom: 'land' | 'sea' | 'point';
  /** The point, in terrain metres, when `startFrom` is `point`. */
  startAt?: [number, number] | null;
  /** The ways in, in the recipes' words. */
  insertionMeans: string[];
  /** creator · voteBefore: read only when there is more than one way in. */
  insertionBy: string;
  extractionMeans: string[];
  extractionBy: string;
  /** Back to the base the same way, the default; false asks for a landing zone of its own. */
  returnToBase?: boolean;
  /** The orders left in a folder on the briefing table, to be found; off by default. */
  ordersFolder?: boolean;
  /** CfgVehicles classes the players find at the base, one per vehicle. */
  vehicles: string[];
  /** What the vehicles were proposed for; a different answer proposes them again. */
  vehiclesFor?: string;
}

/** What the last step decides, kept so going back and forth loses nothing. */
export interface MissionDraftDecide {
  /**
   * The recipe the supports were filled from. They are the recipe's own until
   * somebody edits them, so a draft kept for another recipe keeps everything
   * but them.
   */
  recipeId: string;
  /** Who decides each live row of the matrix. */
  by: Record<string, string>;
  /** Null until filled from the recipe. */
  supports: Record<string, number> | null;
  /** Null while nobody touched the list: the seed and the recipe decide. */
  chosenTwists: string[] | null;
  /** The type's own settings chosen here (release D, D6.3), by id; absent is every default. */
  settings?: Record<string, string>;
  /**
   * Now, a date, or a session with no date started by hand. A draft kept from
   * before 2026-10-19 may still say `onPlayers` (the night that started by
   * itself, dropped by the owner): it is read as `onStart`.
   */
  whenMode: 'now' | 'date' | 'onStart' | 'onPlayers';
  whenAt: string;
  /** Who sees the mission: the group's level unless its author says private. Absent is the group's. */
  visibility?: 'PUBLIC' | 'PRIVATE';
  /** Absent until a server is picked or proposed; null is "no server", chosen on purpose. */
  serverId?: number | null;
  title: string;
}

/** Reads any of the wizard's session-storage drafts. */
export function readStored<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Writes any of the wizard's session-storage drafts whole. */
export function writeStored<T>(key: string, value: T): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // private mode: the draft lives in memory only
  }
}

/** Changes some fields of a stored draft, when there is one; `undefined` removes a field. */
export function patchStored<T extends object>(key: string, patch: Partial<T>): void {
  const current = readStored<T>(key);
  if (current) writeStored(key, { ...current, ...patch });
}

/** The draft while it is still being filled: every screen adds its part. */
export type PartialDraft = Partial<MissionDraftStep1>;

export function readDraft(): PartialDraft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as PartialDraft) : {};
  } catch {
    return {};
  }
}

export function writeDraft(patch: PartialDraft): PartialDraft {
  const next = { ...readDraft(), ...patch };
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
  } catch {
    // private mode: the draft lives in memory only
  }
  return next;
}

/**
 * Each step's number, by what it asks. The rail and its summaries key on these
 * and never on a literal: the type's summary sat under "Intel" for as long as
 * a `4:` written by hand said so (QUICK_START_REVIEW.md F1). "Sortida" was
 * added as step 7 on 2026-09-10 by adding its key here.
 */
export const STEP = {
  // Where the night is played, which is what decides the mods (2026-09-19).
  server: 1,
  // The type before the map (2026-09-13, the owner's ask): the recipe decides
  // which ground it needs — an airfield, a site, a bridge — so the map step can
  // say which terrains carry it instead of the type step greying itself out
  // after the terrain was chosen blind.
  type: 2,
  map: 3,
  enemy: 4,
  intel: 5,
  ours: 6,
  start: 7,
  decide: 8,
} as const;

export type Step = (typeof STEP)[keyof typeof STEP];

/** What each done step chose, one line per step, shown under its label in the rail. */
export type StepSummaries = Partial<Record<Step, string>>;

/**
 * The summaries the rail shows for the steps already done, from the drafts:
 * the first carries the server, the map, the enemy, the intel and our
 * faction; the second the type; the third where the night starts.
 */
export function summariesOf(
  draft: PartialDraft,
  type?: MissionDraftStep2 | null,
  start?: MissionDraftStart | null,
): StepSummaries {
  const out: StepSummaries = {};
  if (draft.serverName) {
    // The era beside the server when it is not the everyday one: it changes what the night is made of.
    const era = draft.era && draft.era !== 'modern' ? ` · ${ca.eras[draft.era] ?? draft.era}` : '';
    out[STEP.server] = `${draft.serverName}${era}`;
  }
  if (draft.worldName) out[STEP.map] = draft.worldDisplayName ?? draft.worldName;
  if (draft.enemyName) out[STEP.enemy] = draft.enemyName;
  if (draft.enemyStrength || draft.enemySkill != null || draft.intelQuality)
    out[STEP.intel] = intelSummary(draft);
  if (type?.recipeId) {
    out[STEP.type] = [type.recipeTitle, ca.missionUi.railPlayers(type.players)]
      .filter(Boolean)
      .join(' · ');
  }
  if (draft.playerFactionName) out[STEP.ours] = draft.playerFactionName;
  if (start?.insertionMeans?.length) {
    const t = ca.missionUi.start;
    const from =
      start.startFrom === 'sea'
        ? t.seaShort
        : start.startFrom === 'point'
          ? t.pointShort
          : t.landShort;
    const ways = [start.insertionMeans, start.extractionMeans]
      .map((means) => (means.length ? means.join(' o ') : '—'))
      .join(' / ');
    out[STEP.start] = `${from} · ${ways}`;
  }
  return out;
}

/**
 * What the difficulty step chose, in the fewest words the rail can show: how
 * many (when it is not the type's own), how good, what is marked. A strength
 * the names do not know is left out rather than printed as its key: the rail
 * never shows the generator's own English words.
 */
function intelSummary(draft: PartialDraft): string {
  const count = draft.enemyStrength
    ? (ca.missionUi.intel.countNames[draft.enemyStrength] ?? null)
    : null;
  const skill =
    draft.enemySkill == null ? null : ca.missionUi.intel.railSkill(formatSkill(draft.enemySkill));
  const marks = (draft.intelMarks ?? 0) + (draft.intelFalse ?? 0);
  const intel = draft.intelArea === false && marks === 0 ? 'a cegues' : `${marks} marques`;
  return [count, skill, intel].filter(Boolean).join(' · ');
}
